/*
 * GALINUR — Product match quiz
 *
 * 3 questions -> weighted score -> one of 3 result panels (server-rendered,
 * revealed by index). Weights and the dynamic "based on your answers"
 * sentence fragments live here because they are tied to the exact
 * question/answer order defined in the section schema (3 question blocks,
 * in order; up to 6 answers each). Copy itself stays fully editable in the
 * Theme Editor — only the underlying weights/fragments are fixed in code.
 *
 * Scoring (0-6 total, "how established the concern looks"):
 *   Q1 (concern):  index 2 ("both") = 2, otherwise 0
 *   Q2 (area):     index 5 ("more than one area") = 2, otherwise 0
 *   Q3 (duration):  <3mo = 0, 3-6mo = 1, 6-12mo = 1, >1yr = 2
 *   total <= 1 -> result 0 (Starter)
 *   total <= 3 -> result 1 (Consistent Routine)
 *   else       -> result 2 (Complete Routine)
 * This takes roughly two "strong" signals to reach the top tier, so a
 * single strong answer does not automatically push toward the priciest
 * bundle.
 *
 * Pricing / cart: the Bundle Up app (an app-embed block, present on this
 * product's page) sells the 2-soap and 3-soap+addons results as discounted
 * bundle tiers, and its discount math is NOT a plain unit-price × quantity
 * calculation. Rather than re-implementing that math (which would drift the
 * moment the merchant changes a Bundle Up offer), this component reads
 * Bundle Up's own rendered offer cards (#bundle-up .bundle-offer-card-N) at
 * runtime: it copies the exact price/compare-price text Bundle Up itself
 * shows for the matching offer index (0/1/2, same order as our 3 results),
 * and — on the CTA click — drives Bundle Up's own offer-select + its real
 * #BU-action-button instead of building our own cart/add.js payload. That
 * makes the displayed price and the real cart price structurally unable to
 * disagree. If Bundle Up's widget isn't found (removed, product changed,
 * app uninstalled), everything falls back to a plain native cart/add.js
 * add using the Liquid-rendered unit-price × quantity fallback text.
 */
(function () {
  if (customElements.get('product-match-quiz')) return;

  var Q1_WEIGHTS = [0, 0, 2];
  var Q2_WEIGHTS = [0, 0, 0, 0, 0, 2];
  var Q3_WEIGHTS = [0, 1, 1, 2];
  var WEIGHTS = [Q1_WEIGHTS, Q2_WEIGHTS, Q3_WEIGHTS];

  var CONCERN_PHRASES = ['לכהויות ולסימנים', 'לפצעונים', 'לפצעונים ולכהויות'];
  var AREA_PHRASES = [
    'באזור הישבן',
    'באזור הגב',
    'באזור הכתפיים',
    'באזור החזה',
    'באזור הירכיים והרגליים',
    'ביותר מאזור אחד',
  ];
  var DURATION_PHRASES = [
    'והמצב חדש יחסית',
    'והמצב מלווה אותך כבר כמה חודשים',
    'והמצב מלווה אותך כבר תקופה',
    'והמצב מלווה אותך כבר יותר משנה',
  ];

  function scoreToResultIndex(total) {
    if (total <= 1) return 0;
    if (total <= 3) return 1;
    return 2;
  }

  function buildSentence(answers) {
    var concern = CONCERN_PHRASES[answers[0]] || '';
    var area = AREA_PHRASES[answers[1]] || '';
    var duration = DURATION_PHRASES[answers[2]] || '';
    return 'לפי התשובות שלך, את מחפשת שגרת טיפוח ' + concern + ' ' + area + ', ' + duration + '.';
  }

  class QuizElement extends HTMLElement {
    connectedCallback() {
      this.stepsWrap = this.querySelector('[data-quiz-steps]');
      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-quiz-step]'));
      this.resultsWrap = this.querySelector('[data-quiz-results]');
      this.resultPanels = Array.prototype.slice.call(this.querySelectorAll('[data-result-panel]'));
      this.progressFill = this.querySelector('[data-quiz-progress-fill]');
      this.progressLabel = this.querySelector('[data-quiz-progress-label]');
      this.backBtn = this.querySelector('[data-quiz-back]');
      this.restartBtn = this.querySelector('[data-quiz-restart]');
      this.sentenceEl = this.querySelector('[data-quiz-sentence]');
      this.progress = this.querySelector('.quiz__progress');

      if (!this.steps.length || !this.resultPanels.length) return;

      this.total = this.steps.length;
      this.progressFormat = this.dataset.progressFormat || '{current} מתוך {total}';
      this.answers = [];
      this.currentStep = 0;

      this.addEventListener('click', this._onClick.bind(this));

      this._updateProgress();
      this._syncBundleUpPricing();
    }

    // --- Bundle Up integration -------------------------------------------

    _findBundleUp() {
      var root = document.getElementById('bundle-up');
      if (!root) return null;
      var cards = [0, 1, 2].map(function (i) {
        return root.querySelector('.bundle-offer-card-' + i);
      });
      var actionBtn = root.querySelector('#BU-action-button');
      if (!actionBtn || cards.some(function (c) { return !c; })) return null;
      return { root: root, cards: cards, actionBtn: actionBtn };
    }

    _syncBundleUpPricing(attempt) {
      attempt = attempt || 0;
      var bu = this._findBundleUp();
      var ready =
        bu &&
        bu.cards.every(function (c) {
          var t = c.querySelector('.offer-price');
          return t && t.textContent.trim();
        });

      if (ready) {
        this.bundleUp = bu;
        this.resultPanels.forEach(function (panel, i) {
          var card = bu.cards[i];
          if (!card) return;
          var priceText = card.querySelector('.offer-price').textContent.trim();
          var compareEl = card.querySelector('.offer-compare-price');
          var compareText = compareEl ? compareEl.textContent.trim() : '';

          var currentOut = panel.querySelector('[data-quiz-price-current]');
          var compareOut = panel.querySelector('[data-quiz-price-compare]');
          if (currentOut && priceText) currentOut.textContent = priceText;
          if (compareOut) {
            if (compareText && compareText !== priceText) {
              compareOut.textContent = compareText;
              compareOut.hidden = false;
            } else {
              compareOut.hidden = true;
            }
          }
        });
        return;
      }

      // Bundle Up renders its prices asynchronously after its own bridge
      // script runs — keep checking briefly, then give up and keep the
      // Liquid-rendered fallback price (plain unit-price × quantity).
      if (attempt < 25) {
        var self = this;
        window.setTimeout(function () {
          self._syncBundleUpPricing(attempt + 1);
        }, 200);
      }
    }

    _onClick(event) {
      var answerBtn = event.target.closest('[data-quiz-answer]');
      if (answerBtn) {
        this._selectAnswer(answerBtn);
        return;
      }
      if (event.target.closest('[data-quiz-back]')) {
        this._goBack();
        return;
      }
      if (event.target.closest('[data-quiz-restart]')) {
        this._restart();
        return;
      }
      var cta = event.target.closest('[data-quiz-cta]');
      if (cta) {
        this._addToCart(cta);
      }
    }

    _selectAnswer(btn) {
      var qIndex = parseInt(btn.dataset.q, 10);
      var aIndex = parseInt(btn.dataset.a, 10);
      this.answers[qIndex] = aIndex;

      var step = this.steps[qIndex];
      Array.prototype.forEach.call(step.querySelectorAll('[data-quiz-answer]'), function (b) {
        b.setAttribute('aria-checked', String(b === btn));
      });

      var self = this;
      window.setTimeout(function () {
        if (qIndex === self.total - 1) {
          self._showResults();
        } else {
          self._goToStep(qIndex + 1);
        }
      }, 220);
    }

    _goToStep(index) {
      this.currentStep = index;
      this.resultsWrap.hidden = true;
      this.progress.hidden = false;
      this.stepsWrap.hidden = false;

      this.steps.forEach(function (step, i) {
        step.hidden = i !== index;
      });

      if (this.backBtn) this.backBtn.hidden = index === 0;
      this._updateProgress();

      var heading = this.steps[index].querySelector('.quiz__question');
      if (heading) heading.setAttribute('tabindex', '-1');
      window.requestAnimationFrame(function () {
        if (heading) heading.focus({ preventScroll: true });
      });
    }

    _goBack() {
      if (this.currentStep === 0) return;
      this._goToStep(this.currentStep - 1);
    }

    _updateProgress() {
      if (!this.progressFill || !this.progressLabel) return;
      var current = this.currentStep + 1;
      var pct = (current / this.total) * 100;
      this.progressFill.style.width = pct + '%';
      this.progressLabel.textContent = this.progressFormat
        .replace('{current}', String(current))
        .replace('{total}', String(this.total));
    }

    _showResults() {
      var total = 0;
      for (var i = 0; i < this.answers.length; i++) {
        var weights = WEIGHTS[i] || [];
        total += weights[this.answers[i]] || 0;
      }
      var resultIndex = scoreToResultIndex(total);

      if (this.sentenceEl) this.sentenceEl.textContent = buildSentence(this.answers);

      this.stepsWrap.hidden = true;
      this.progress.hidden = true;
      if (this.backBtn) this.backBtn.hidden = true;
      this.resultsWrap.hidden = false;

      this.resultPanels.forEach(function (panel, i) {
        panel.hidden = i !== resultIndex;
      });

      var activePanel = this.resultPanels[resultIndex];
      window.requestAnimationFrame(function () {
        if (activePanel) activePanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }

    _restart() {
      this.answers = [];
      this.resultPanels.forEach(function (panel) {
        panel.hidden = true;
      });
      this.steps.forEach(function (step) {
        Array.prototype.forEach.call(step.querySelectorAll('[data-quiz-answer]'), function (b) {
          b.setAttribute('aria-checked', 'false');
        });
      });
      this._goToStep(0);
    }

    _addToCart(btn) {
      if (btn.getAttribute('aria-busy') === 'true') return;

      var resultIndex = parseInt(btn.dataset.resultKey, 10);
      var bu = this.bundleUp;
      if (bu && !isNaN(resultIndex) && bu.cards[resultIndex]) {
        this._addViaBundleUp(btn, bu, resultIndex);
      } else {
        this._addNative(btn);
      }
    }

    // Drive Bundle Up's own widget: select the matching offer card, then
    // click Bundle Up's own add-to-cart button. This is the one path that
    // can never disagree with the price we displayed, because it doesn't
    // duplicate Bundle Up's logic — it triggers the exact same code path a
    // customer clicking the widget directly would trigger (its own cart
    // drawer / confirmation included), so we deliberately do not also
    // redirect afterward.
    _addViaBundleUp(btn, bu, resultIndex) {
      btn.setAttribute('aria-busy', 'true');
      var originalText = btn.textContent;
      btn.textContent = '…';

      var card = bu.cards[resultIndex];
      if (!card.classList.contains('selectedBU')) {
        card.click();
      }

      window.setTimeout(function () {
        bu.actionBtn.click();
        window.setTimeout(function () {
          btn.removeAttribute('aria-busy');
          btn.textContent = originalText;
        }, 1200);
      }, 150);
    }

    // Fallback used only if Bundle Up's widget can't be found on the page
    // (e.g. the app is off, or this section gets reused on a product
    // without it) — plain native cart add at the Liquid-rendered fallback
    // price (unit price × quantity, no bundle discount).
    _addNative(btn) {
      var items = [];
      var variantId = btn.dataset.variantId;
      if (!variantId) return;
      items.push({ id: parseInt(variantId, 10), quantity: parseInt(btn.dataset.quantity, 10) || 1, properties: { _מקור: 'שאלון התאמה' } });

      if (btn.dataset.addon1VariantId) {
        items.push({ id: parseInt(btn.dataset.addon1VariantId, 10), quantity: parseInt(btn.dataset.addon1Quantity, 10) || 1 });
      }
      if (btn.dataset.addon2VariantId) {
        items.push({ id: parseInt(btn.dataset.addon2VariantId, 10), quantity: parseInt(btn.dataset.addon2Quantity, 10) || 1 });
      }

      btn.setAttribute('aria-busy', 'true');
      var originalText = btn.textContent;
      btn.textContent = '…';

      fetch(window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error('cart add failed');
          return res.json();
        })
        .then(function () {
          window.location.href = (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart';
        })
        .catch(function () {
          btn.removeAttribute('aria-busy');
          btn.textContent = originalText;
          window.location.href = '/cart';
        });
    }
  }

  customElements.define('product-match-quiz', QuizElement);
})();
